import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Mail, Linkedin, Globe, MapPin, Briefcase, Save, X,
    Star, StarOff, UserPlus, Trash2, Edit2, Check, Plus,
} from 'lucide-react';

import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { AddToPipelineModal } from './AddToPipelineModal.jsx';
import { SingleSelect, MultiSelect } from './DropdownField.jsx';
import './InvestorDetail.css';

export function InvestorDetail({ investorId, onUpdated }) {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const isStaff = user?.role === 'admin' || user?.role === 'team';
    const [editing, setEditing] = useState(false);
    const [formState, setFormState] = useState({});
    const [pipelineModalOpen, setPipelineModalOpen] = useState(false);

    // Load dropdown options + users for "Introduced by" dropdown (staff only)
    const { data: settings } = useQuery({
        queryKey: ['settings'],
        queryFn: () => api.get('/settings'),
    });
    const { data: usersList } = useQuery({
        queryKey: ['users'],
        queryFn: () => api.get('/auth/users'),
        enabled: isStaff,
    });

    const { data: investor, isLoading, error } = useQuery({
        queryKey: ['investor', investorId],
        queryFn: () => api.get(`/investors/${investorId}`),
        enabled: investorId != null,
    });

    const saveMutation = useMutation({
        mutationFn: (updates) => api.put(`/investors/${investorId}`, updates),
        onSuccess: (updated) => {
            queryClient.setQueryData(['investor', investorId], updated);
            setEditing(false);
            onUpdated?.();
        },
    });

    useEffect(() => {
        if (investor && editing) setFormState({ ...investor });
        if (!editing) setFormState({});
    }, [investor, editing]);

    if (investorId == null) {
        return (
            <div className="detail-empty">
                <div className="detail-empty-inner">
                    <div className="detail-empty-glyph">B</div>
                    <p>Select an investor to see details</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="detail-empty">
                <span className="spinner" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="detail-empty detail-empty-error">
                <strong>Couldn't load investor</strong>
                <span>{error.message}</span>
            </div>
        );
    }

    // Client without access — show blurred placeholder with request button
    if (investor?._access && !investor._access.has_access) {
        return (
            <RestrictedView
                investor={investor}
                onRequested={() => queryClient.invalidateQueries({ queryKey: ['investor', investorId] })}
            />
        );
    }

    const inv = editing ? formState : investor;

    const setField = (field) => (e) => {
        setFormState(prev => ({ ...prev, [field]: e.target.value }));
    };

    // Direct value setter — for dropdowns that pass the value, not an event
    const setValue = (field) => (newValue) => {
        setFormState(prev => ({ ...prev, [field]: newValue }));
    };

    // Build dropdown options from settings (loaded above)
    const sectorOptions     = Array.isArray(settings?.sectors)            ? settings.sectors            : [];
    const investorTypeOpts  = Array.isArray(settings?.investor_types)     ? settings.investor_types     : [];
    const stageOptions      = Array.isArray(settings?.investment_stages)  ? settings.investment_stages  : [];
    const financingOptions  = Array.isArray(settings?.financing_types)    ? settings.financing_types    : [];
    const geoOptions        = Array.isArray(settings?.geo_options)        ? settings.geo_options        : [];
    const fitLevelOptions   = Array.isArray(settings?.fit_levels)         ? settings.fit_levels         : [];
    const statusOptions     = Array.isArray(settings?.status_options)     ? settings.status_options     : [];

    const handleSave = () => {
        const diff = {};
        for (const [k, v] of Object.entries(formState)) {
            // Don't try to send computed/nested fields back
            if (k === 'contacts' || k === 'activity' || k === 'primary_contact_name' ||
                k === 'primary_contact_email' || k === 'primary_contact_linkedin' ||
                k === 'contact_count') continue;
            if (v !== investor[k]) diff[k] = v;
        }
        if (Object.keys(diff).length > 0) saveMutation.mutate(diff);
        else setEditing(false);
    };

    return (
        <div className="detail">
            <header className="detail-header">
                <div className="detail-header-main">
                    {editing ? (
                        <input
                            className="detail-title-input"
                            value={inv.name || ''}
                            onChange={setField('name')}
                        />
                    ) : (
                        <h2 className="detail-title">{inv.name}</h2>
                    )}
                    <div className="detail-subheader">
                        {inv.investor_type && <span>{inv.investor_type}</span>}
                        {inv.country && (
                            <>
                                <span className="dot-sep">·</span>
                                <span><MapPin size={11} strokeWidth={1.75}/> {inv.country}</span>
                            </>
                        )}
                        {(inv.owner_name || inv.owner_text) && (
                            <>
                                <span className="dot-sep">·</span>
                                <span>Introduced by: <strong>{inv.owner_name || inv.owner_text}</strong></span>
                            </>
                        )}
                    </div>
                </div>

                <div className="detail-header-actions">
                    {editing ? (
                        <>
                            <button className="btn-ghost" onClick={() => setEditing(false)}
                                    disabled={saveMutation.isPending}>
                                <X size={14} strokeWidth={2}/> Cancel
                            </button>
                            <button className="btn-save" onClick={handleSave}
                                    disabled={saveMutation.isPending}>
                                {saveMutation.isPending
                                    ? <span className="spinner" />
                                    : <><Save size={14} strokeWidth={2}/> Save changes</>}
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="btn-secondary" onClick={() => setPipelineModalOpen(true)}>
                                <Plus size={14} strokeWidth={2}/> Add to deals
                            </button>
                            <button className="btn-edit" onClick={() => setEditing(true)}>Edit</button>
                        </>
                    )}
                </div>
            </header>

            {saveMutation.isError && (
                <div className="detail-error">
                    Failed to save: {saveMutation.error.message}
                </div>
            )}

            <div className="detail-body">
                {/* Contacts is now a first-class panel, not a set of fields */}
                <ContactsPanel
                    investorId={investorId}
                    contacts={investor.contacts || []}
                />

                <Section title="Status & Fit">
                    <DropdownLabel label="Status" editing={editing} value={inv.status}>
                        <SingleSelect value={inv.status} options={statusOptions}
                                      onChange={setValue('status')} placeholder="Status..."/>
                    </DropdownLabel>
                    <Field label="Stage" value={inv.stage} editing={editing} onChange={setField('stage')} />
                    <DropdownLabel label="Fit level" editing={editing} value={inv.fit_level}>
                        <SingleSelect value={inv.fit_level} options={fitLevelOptions}
                                      onChange={setValue('fit_level')} placeholder="Fit level..."/>
                    </DropdownLabel>
                    <Field label="Next action date" value={inv.next_action_date} editing={editing}
                           onChange={setField('next_action_date')} />
                    {/* Introduced by — staff-only edit; others see read-only */}
                    {editing && isStaff ? (
                        <DropdownLabel label="Introduced by" editing={editing}
                                       value={inv.owner_name || inv.owner_text}>
                            <IntroducedBySelect
                                value={inv.owner_user_id}
                                fallbackText={inv.owner_text}
                                users={usersList}
                                onChange={(userId, text) => {
                                    setFormState(prev => ({
                                        ...prev,
                                        owner_user_id: userId,
                                        owner_text: text,  // keep in sync; null if matched user
                                    }));
                                }}
                            />
                        </DropdownLabel>
                    ) : (
                        <Field label="Introduced by"
                               value={inv.owner_name || inv.owner_text || ''}
                               editing={false}/>
                    )}
                </Section>

                <Section title="Investment profile">
                    <DropdownLabel label="Investor type" editing={editing} value={inv.investor_type}>
                        <SingleSelect value={inv.investor_type} options={investorTypeOpts}
                                      onChange={setValue('investor_type')} placeholder="Type of investor..."/>
                    </DropdownLabel>
                    <DropdownLabel label="Sector fit" editing={editing} value={inv.sector_fit}>
                        <MultiSelect value={inv.sector_fit} options={sectorOptions}
                                     onChange={setValue('sector_fit')} placeholder="Sectors..."/>
                    </DropdownLabel>
                    <DropdownLabel label="Financing type" editing={editing} value={inv.financing_type}>
                        <MultiSelect value={inv.financing_type} options={financingOptions}
                                     onChange={setValue('financing_type')} placeholder="Equity, Debt, Grants..."/>
                    </DropdownLabel>
                    <DropdownLabel label="Investment stage" editing={editing} value={inv.investment_stage}>
                        <MultiSelect value={inv.investment_stage} options={stageOptions}
                                     onChange={setValue('investment_stage')} placeholder="Pre-seed, Seed, Series A..."/>
                    </DropdownLabel>
                    <DropdownLabel label="Geographic focus" editing={editing} value={inv.geo_focus}>
                        <MultiSelect value={inv.geo_focus} options={geoOptions}
                                     onChange={setValue('geo_focus')} placeholder="Europe, DACH, Benelux..."/>
                    </DropdownLabel>
                    <Field label="Ticket sweetspot" value={inv.expected_ticket} editing={editing}
                           onChange={setField('expected_ticket')} />
                    <Field label="Range (min)" value={inv.investment_range_min} editing={editing}
                           onChange={setField('investment_range_min')} mono/>
                    <Field label="Range (max)" value={inv.investment_range_max} editing={editing}
                           onChange={setField('investment_range_max')} mono/>
                </Section>

                <Section title="Links & Pipeline">
                    <Field label="Website" value={inv.website} editing={editing}
                           onChange={setField('website')}
                           icon={<Globe size={13} strokeWidth={1.75}/>} link={inv.website}/>
                    <Field label="Assigned to startups" value={inv.assign_to_startups} editing={editing}
                           onChange={setField('assign_to_startups')}
                           icon={<Briefcase size={13} strokeWidth={1.75}/>}/>
                </Section>

                <Section title="Notes" full>
                    <Field label="Fit rationale" value={inv.fit_rationale} editing={editing}
                           onChange={setField('fit_rationale')} multiline />
                    <Field label="Notes" value={inv.notes} editing={editing}
                           onChange={setField('notes')} multiline />
                </Section>

                {investor.activity && investor.activity.length > 0 && (
                    <Section title="Recent activity" full>
                        <ul className="activity-list">
                            {investor.activity.slice(0, 10).map((a, i) => (
                                <li key={i}>
                                    <span className="activity-user">{a.user_email || 'system'}</span>
                                    <span className="activity-action">{a.action}</span>
                                    <time className="activity-time mono">
                                        {formatActivityTime(a.created_at)}
                                    </time>
                                </li>
                            ))}
                        </ul>
                    </Section>
                )}
            </div>

            {pipelineModalOpen && (
                <AddToPipelineModal
                    investor={investor}
                    onClose={() => setPipelineModalOpen(false)}
                    onCreated={() => onUpdated?.()}
                />
            )}
        </div>
    );
}

/* ==========================================================================
 * Contacts Panel — full CRUD for contacts under a fund
 * ========================================================================== */
function ContactsPanel({ investorId, contacts }) {
    const queryClient = useQueryClient();
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['investor', investorId] });
    const invalidateList = () => queryClient.invalidateQueries({ queryKey: ['investors'] });

    const addMutation = useMutation({
        mutationFn: (contact) => api.post(`/investors/${investorId}/contacts`, contact),
        onSuccess: () => { invalidate(); invalidateList(); setAdding(false); },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, updates }) => api.put(`/investors/${investorId}/contacts/${id}`, updates),
        onSuccess: () => { invalidate(); invalidateList(); setEditingId(null); },
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => api.delete(`/investors/${investorId}/contacts/${id}`),
        onSuccess: () => { invalidate(); invalidateList(); },
    });

    const makePrimary = (contactId) => {
        updateMutation.mutate({ id: contactId, updates: { is_primary: true } });
    };

    const remove = (contactId, name) => {
        if (confirm(`Remove contact ${name || '(unnamed)'}?`)) {
            deleteMutation.mutate(contactId);
        }
    };

    return (
        <section className="detail-section detail-section-full contacts-panel">
            <div className="contacts-panel-header">
                <h3 className="detail-section-title">
                    Contacts <span className="contacts-count mono">{contacts.length}</span>
                </h3>
                {!adding && (
                    <button className="btn-ghost btn-ghost-small" onClick={() => setAdding(true)}>
                        <UserPlus size={13} strokeWidth={2}/> Add contact
                    </button>
                )}
            </div>

            {adding && (
                <ContactForm
                    onSubmit={(contact) => addMutation.mutate(contact)}
                    onCancel={() => setAdding(false)}
                    submitting={addMutation.isPending}
                    error={addMutation.error}
                />
            )}

            {contacts.length === 0 && !adding && (
                <p className="contacts-empty">
                    No contacts yet. Use <em>Add contact</em> to register the first person at this fund.
                </p>
            )}

            <ul className="contacts-list">
                {contacts.map(c => (
                    <li key={c.id} className={`contact-card${c.is_primary ? ' is-primary' : ''}`}>
                        {editingId === c.id ? (
                            <ContactForm
                                initial={c}
                                onSubmit={(updates) => updateMutation.mutate({ id: c.id, updates })}
                                onCancel={() => setEditingId(null)}
                                submitting={updateMutation.isPending}
                                error={updateMutation.error}
                            />
                        ) : (
                            <>
                                <div className="contact-avatar">
                                    {initials(c.name || c.email)}
                                </div>

                                <div className="contact-body">
                                    <div className="contact-row-top">
                                        <span className="contact-name">{c.name || '(no name)'}</span>
                                        {c.is_primary && (
                                            <span className="primary-pill">
                                                <Star size={10} strokeWidth={2} /> Primary
                                            </span>
                                        )}
                                        {c.role && <span className="contact-role">{c.role}</span>}
                                    </div>
                                    <div className="contact-row-links">
                                        {c.email && (
                                            <a href={`mailto:${c.email}`} className="contact-link">
                                                <Mail size={12} strokeWidth={1.75}/> {c.email}
                                            </a>
                                        )}
                                        {c.linkedin && (
                                            <a href={c.linkedin} target="_blank" rel="noopener noreferrer"
                                               className="contact-link">
                                                <Linkedin size={12} strokeWidth={1.75}/> LinkedIn
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <div className="contact-actions">
                                    {!c.is_primary && (
                                        <button
                                            className="contact-action-btn"
                                            onClick={() => makePrimary(c.id)}
                                            title="Make primary contact"
                                            disabled={updateMutation.isPending}
                                        >
                                            <StarOff size={13} strokeWidth={1.75}/>
                                        </button>
                                    )}
                                    <button
                                        className="contact-action-btn"
                                        onClick={() => setEditingId(c.id)}
                                        title="Edit contact"
                                    >
                                        <Edit2 size={13} strokeWidth={1.75}/>
                                    </button>
                                    <button
                                        className="contact-action-btn contact-action-danger"
                                        onClick={() => remove(c.id, c.name)}
                                        title="Delete contact"
                                        disabled={deleteMutation.isPending}
                                    >
                                        <Trash2 size={13} strokeWidth={1.75}/>
                                    </button>
                                </div>
                            </>
                        )}
                    </li>
                ))}
            </ul>
        </section>
    );
}

/* ==========================================================================
 * ContactForm — used both for adding and editing
 * ========================================================================== */
function ContactForm({ initial = {}, onSubmit, onCancel, submitting, error }) {
    const [state, setState] = useState({
        name:     initial.name || '',
        email:    initial.email || '',
        linkedin: initial.linkedin || '',
        role:     initial.role || '',
    });

    const set = (field) => (e) => setState(prev => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        // Only send non-empty fields
        const payload = {};
        for (const [k, v] of Object.entries(state)) {
            if (v.trim() !== '') payload[k] = v.trim();
            else if (initial[k]) payload[k] = null;  // clear existing value
        }
        onSubmit(payload);
    };

    return (
        <form className="contact-form" onSubmit={handleSubmit}>
            <div className="contact-form-grid">
                <label className="detail-field">
                    <span className="detail-field-label">Name</span>
                    <input className="detail-field-input" value={state.name}
                           onChange={set('name')} placeholder="Jane Doe" autoFocus/>
                </label>
                <label className="detail-field">
                    <span className="detail-field-label">Email</span>
                    <input className="detail-field-input" type="email" value={state.email}
                           onChange={set('email')} placeholder="jane@fund.com"/>
                </label>
                <label className="detail-field">
                    <span className="detail-field-label">LinkedIn</span>
                    <input className="detail-field-input" value={state.linkedin}
                           onChange={set('linkedin')} placeholder="https://linkedin.com/in/..."/>
                </label>
                <label className="detail-field">
                    <span className="detail-field-label">Role</span>
                    <input className="detail-field-input" value={state.role}
                           onChange={set('role')} placeholder="Partner, Analyst, ..."/>
                </label>
            </div>

            {error && <div className="detail-error">{error.message}</div>}

            <div className="contact-form-actions">
                <button type="button" className="btn-ghost" onClick={onCancel} disabled={submitting}>
                    <X size={14} strokeWidth={2}/> Cancel
                </button>
                <button type="submit" className="btn-save" disabled={submitting}>
                    {submitting ? <span className="spinner"/> : <><Check size={14} strokeWidth={2}/> Save</>}
                </button>
            </div>
        </form>
    );
}

/* -------------------------------------------------------------------------- */
function Section({ title, children, full = false }) {
    return (
        <section className={`detail-section${full ? ' detail-section-full' : ''}`}>
            <h3 className="detail-section-title">{title}</h3>
            <div className="detail-fields">{children}</div>
        </section>
    );
}

function Field({ label, value, editing, onChange, icon, link, multiline, mono }) {
    if (editing) {
        return (
            <label className="detail-field">
                <span className="detail-field-label">{label}</span>
                {multiline ? (
                    <textarea value={value ?? ''} onChange={onChange} rows={3}
                              className="detail-field-input"/>
                ) : (
                    <input type="text" value={value ?? ''} onChange={onChange}
                           className="detail-field-input"/>
                )}
            </label>
        );
    }

    const display = value === null || value === undefined || value === '' ? null : value;

    return (
        <div className="detail-field">
            <span className="detail-field-label">{label}</span>
            {display == null ? (
                <span className="detail-field-empty">—</span>
            ) : link ? (
                <a href={link} target="_blank" rel="noopener noreferrer"
                   className="detail-field-value detail-field-link">
                    {icon}{display}
                </a>
            ) : (
                <span className={`detail-field-value${mono ? ' mono' : ''}${multiline ? ' detail-field-multiline' : ''}`}>
                    {icon}{display}
                </span>
            )}
        </div>
    );
}

function initials(s) {
    if (!s) return '?';
    const parts = s.trim().split(/\s+/).slice(0, 2);
    return parts.map(p => p[0]).join('').toUpperCase();
}

/* ==========================================================================
 * DropdownLabel — wraps a SingleSelect/MultiSelect in the standard label/row
 * layout used by Field, so dropdowns blend naturally with text fields.
 * In read-mode, displays the value as plain text or chips.
 * ========================================================================== */
function DropdownLabel({ label, editing, value, children }) {
    return (
        <div className="detail-field">
            <span className="detail-field-label">{label}</span>
            {editing ? children : (
                <span className="detail-field-value detail-field-multivalue">
                    {value
                        ? value.toString().split(/,\s*/).filter(Boolean).map((v, i) => (
                            <span key={i} className="detail-field-chip">{v}</span>
                          ))
                        : <span className="detail-field-empty">—</span>}
                </span>
            )}
        </div>
    );
}

/* ==========================================================================
 * IntroducedBySelect — picks a user from the system to credit as introducer.
 * Falls back to free-text owner_text for legacy values that aren't users yet.
 * ========================================================================== */
function IntroducedBySelect({ value, fallbackText, users, onChange }) {
    // If we have a users list, render a SingleSelect with their names.
    // Otherwise: show a notice that we couldn't load users.
    if (!users) {
        return <span className="detail-field-empty">Loading users…</span>;
    }
    const activeUsers = users.filter(u => u.is_active !== 0);
    const labelByUser = (u) => u.name || u.email;
    const selectedLabel = value
        ? labelByUser(activeUsers.find(u => u.id === value) || {})
        : (fallbackText || '');
    const optionLabels = activeUsers.map(labelByUser);
    return (
        <SingleSelect
            value={selectedLabel}
            options={optionLabels}
            onChange={(label) => {
                if (!label) {
                    onChange(null, null);
                    return;
                }
                const matched = activeUsers.find(u => labelByUser(u) === label);
                if (matched) onChange(matched.id, null);
                else onChange(null, label);  // free text fallback
            }}
            placeholder="Choose a team member or client..."
            allowClear={true}
        />
    );
}

function formatActivityTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86_400_000);
    if (diffDays < 1) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
}

/* ==========================================================================
 * RestrictedView — client doesn't have access to this investor's full details
 * ========================================================================== */
function RestrictedView({ investor, onRequested }) {
    const access = investor._access || {};
    const hasPending = !!access.pending_request_id;

    const requestMutation = useMutation({
        mutationFn: (reason) => api.post('/access-requests', {
            investor_id: investor.id,
            reason: reason || undefined,
        }),
        onSuccess: () => onRequested?.(),
    });

    const [showForm, setShowForm] = useState(false);
    const [reason, setReason] = useState('');

    return (
        <div className="detail">
            <header className="detail-header">
                <div className="detail-header-main">
                    <h2 className="detail-title">{investor.name}</h2>
                    <div className="detail-subheader">
                        <span>Restricted access — request approval to see full details</span>
                    </div>
                </div>
            </header>

            <div className="detail-body">
                <div className="restricted-card">
                    <div className="restricted-blur">
                        <div className="blur-line blur-line-lg"/>
                        <div className="blur-line"/>
                        <div className="blur-line blur-line-md"/>
                        <div className="blur-line"/>
                        <div className="blur-line blur-line-sm"/>
                    </div>

                    <div className="restricted-overlay">
                        <h3>Need access to this investor?</h3>
                        <p>
                            Behold curates the investor database carefully. To unlock the full record
                            (contact details, notes, fit rationale, history), request access from the team.
                            Once approved, this investor stays in your view permanently.
                        </p>

                        {hasPending ? (
                            <div className="restricted-pending">
                                <Check size={14} strokeWidth={2}/>
                                Request submitted — awaiting decision
                            </div>
                        ) : showForm ? (
                            <form
                                className="restricted-form"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    requestMutation.mutate(reason.trim() || null);
                                }}
                            >
                                <textarea
                                    className="detail-field-input"
                                    rows={3}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Why do you want to reach out? (optional, helps the team decide)"
                                    autoFocus
                                />
                                {requestMutation.isError && (
                                    <div className="panel-error">{requestMutation.error.message}</div>
                                )}
                                <div className="restricted-actions">
                                    <button type="button" className="btn-ghost"
                                            onClick={() => setShowForm(false)}>
                                        <X size={14} strokeWidth={2}/> Cancel
                                    </button>
                                    <button type="submit" className="btn-save"
                                            disabled={requestMutation.isPending}>
                                        {requestMutation.isPending
                                            ? <span className="spinner"/>
                                            : <><Check size={14} strokeWidth={2}/> Submit request</>}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <button className="btn-secondary" onClick={() => setShowForm(true)}>
                                Request access
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
